import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { Grid } from '@naf/grid';
import { TextVariant } from '@naf/text';
import { HttpStatus } from '@components/HttpStatus';
import { LayoutWithMainContent } from '@components/layout/Layout';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import { MAIN_URL } from '@lib/constants';
import * as S from './NotFound.styles';

interface NotFoundProps {
  withoutLayout?: boolean;
}

export const NotFound = ({ withoutLayout = false }: NotFoundProps) =>
  // The 404 status is surfaced to the server renderer via <HttpStatus code={404}> inside NotFoundInner.
  withoutLayout ? (
    <NotFoundInner />
  ) : (
    <LayoutWithMainContent
      title="NAF - 404"
      description="404 - Vi finner ikke siden du leter etter"
      url=""
      imgUrl=""
      disableCanonical
      internalSearchMetaData={{ cardType: InternalSearchCardType.Simple }}
      isHiddenFromSearch
    >
      <NotFoundInner />
    </LayoutWithMainContent>
  );

const NotFoundInner = () => {
  const title = 'Kjørt deg fast?';
  const description = 'Trykk på pilene for å finne veien videre, eller gå tilbake til forsiden.';
  const buttonText = 'Gå til forsiden';

  const [baseUrl, setBaseUrl] = useState(MAIN_URL);

  useEffect(() => {
    setBaseUrl(window.location.origin);
  }, []);

  const [NotFoundImage, setNotFoundImage] = useState<null | ReactNode>(null);

  const handleVehicleClick = useCallback(() => {
    if (baseUrl) {
      window.location.href = `${baseUrl}/bilverksted-og-tester`;
    }
  }, [baseUrl]);

  const handleElbilClick = useCallback(() => {
    if (baseUrl) {
      window.location.href = `${baseUrl}/elbil`;
    }
  }, [baseUrl]);

  const handleBenefitsClick = useCallback(() => {
    if (baseUrl) {
      window.location.href = `${baseUrl}/medlemskap/medlemsfordeler`;
    }
  }, [baseUrl]);

  const handleTravelClick = useCallback(() => {
    if (baseUrl) {
      window.location.href = `${baseUrl}/bilferie-og-reise`;
    }
  }, [baseUrl]);

  useEffect(() => {
    const dynamicImport = async () => {
      const dynamicallyImportedComponent = await import('@components/NotFoundImage');
      const Component = dynamicallyImportedComponent.default;
      setNotFoundImage(
        <Component
          onVehicleClick={handleVehicleClick}
          onElbilClick={handleElbilClick}
          onBenefitsClick={handleBenefitsClick}
          onTravelClick={handleTravelClick}
        />,
      );
    };
    dynamicImport();
  }, [handleVehicleClick, handleElbilClick, handleBenefitsClick, handleTravelClick]);

  return (
    <S.Container role="main">
      <S.Wrapper>
        <Grid>
          <S.StyledGridCol s="12" m="12" l="6" xl="6">
            <HttpStatus code={404}>
              <S.Header variant={TextVariant.Header1}>{title}</S.Header>
              <S.StyledText tag="p" variant={TextVariant.ArticleTextLarge}>
                {description}
              </S.StyledText>
              <S.StyledButton href={baseUrl}>{buttonText}</S.StyledButton>
              <S.StyledButtonMobile href={baseUrl}>{buttonText}</S.StyledButtonMobile>
            </HttpStatus>
          </S.StyledGridCol>
          <S.StyledImageGridCol s="12" m="12" l="6" xl="6">
            {NotFoundImage}
          </S.StyledImageGridCol>
        </Grid>
      </S.Wrapper>
    </S.Container>
  );
};
